import { useState } from 'react'
import { GridColumn as Column, Grid } from '@progress/kendo-react-grid'
import { Button } from 'antd'
import { AppropriatenessTestDto } from 'api/Clients/Types/AppropriatenessTestDto'
import { Option, Select } from 'components/Select'
import TableWithExport from 'utils/table/withExport'

import { scoreSettingsColumns } from './columns'

import styles from './ScoreSettingsTable.module.scss'

const passOptions = Array.from({ length: 4 }, (_, i) => ({
  value: (i + 1).toString(),
  label: `${i + 1} and above`,
}))

const failOptions = Array.from({ length: 5 }, (_, i) => ({
  value: i.toString(),
  label: `${i} and below`,
}))

interface ScoreRow {
  pass: string
  fail: string
  marginallyPass: string
  error?: string
}

interface ScoreSettingsTableProps {
  visible: boolean
  onClose: () => void
  onSave: (data: ScoreRow) => void
  testData: AppropriatenessTestDto[]
}

const ScoreSettingsTable: React.FC<ScoreSettingsTableProps> = ({
  visible,
  onClose,
  onSave,
  testData,
}) => {
  const test = testData[0]

  const [row, setRow] = useState<ScoreRow>({
    pass: test?.maxReferredScore?.toString() || '',
    fail: test?.maxFailScore?.toString() || '',
    marginallyPass: test?.marginallyPass || '',
    error: '',
  })

  const calculateMarginallyPass = (pass: string, fail: string): string => {
    const maxReferredScore = parseInt(pass, 10)
    const maxFailScore = parseInt(fail, 10)
    if (Number.isNaN(maxReferredScore) || Number.isNaN(maxFailScore)) return ''
    if (maxReferredScore < 0 || maxFailScore < 0 || maxReferredScore - maxFailScore <= 1)
      return 'NA'
    return maxReferredScore - maxFailScore === 2
      ? `${maxFailScore + 1}`
      : `${maxFailScore + 1} - ${maxReferredScore - 1}`
  }

  const validate = (updated: ScoreRow): string | undefined => {
    const p = parseInt(updated.pass, 10)
    const f = parseInt(updated.fail, 10)
    if (Number.isNaN(p) || Number.isNaN(f)) return undefined
    if (p <= f) return 'Pass score must be greater than Fail score.'
    const diff = p - f
    if (diff <= 1) return 'Marginally Pass score must be more than 1.'
  }

  const update = (field: 'pass' | 'fail', value: string) => {
    const updated: ScoreRow = { ...row, [field]: value } as ScoreRow
    if (updated.pass && updated.fail) {
      updated.marginallyPass = calculateMarginallyPass(updated.pass, updated.fail)
      const validationError = validate(updated)
      updated.error = validationError || ''
    } else {
      updated.marginallyPass = ''
      updated.error = ''
    }
    setRow(updated)
  }

  if (!visible) return null
  const saveDisabled = !!row.error || !row.pass || !row.fail || row.marginallyPass === 'NA'

  return (
    <div className={styles.scoreSettingsContainer}>
      <div className={styles.header}>
        <span className={styles.title}>Score Settings</span>
        <div className={styles.buttonGroup}>
          <Button onClick={onClose} className={styles.closeButton}>
            Close
          </Button>
          <Button disabled={saveDisabled} onClick={() => onSave(row)}>
            Save
          </Button>
        </div>
      </div>
      <TableWithExport data={[test]} columns={scoreSettingsColumns}>
        <Grid data={[test]} style={{ zIndex: 0 }}>
          {scoreSettingsColumns.map((column, idx) => (
            <Column
              key={idx}
              {...column}
              cell={(props) => {
                if (column.field === 'pass') {
                  return (
                    <td>
                      <Select
                        value={row.pass}
                        className={styles.select}
                        onChange={(v) => update('pass', v)}
                      >
                        {passOptions.map((opt) => (
                          <Option key={opt.value} value={opt.value}>
                            {opt.label}
                          </Option>
                        ))}
                      </Select>
                    </td>
                  )
                }
                if (column.field === 'fail') {
                  return (
                    <td>
                      <Select
                        value={row.fail}
                        className={styles.select}
                        onChange={(v) => update('fail', v)}
                      >
                        {failOptions.map((opt) => (
                          <Option key={opt.value} value={opt.value}>
                            {opt.label}
                          </Option>
                        ))}
                      </Select>
                    </td>
                  )
                }
                if (column.field === 'marginallyPass') {
                  return <td>{row.marginallyPass || '-'}</td>
                }
                if (column.field === 'tickmillProduct.name') {
                  return <td>{props.dataItem?.tickmillProduct?.name || '-'}</td>
                }
                return <td>{column.field ? props.dataItem[column.field] : null}</td>
              }}
            />
          ))}
        </Grid>
      </TableWithExport>
      {row.error ? (
        <div className={styles.errorMessage}>
          <span>{row.error}</span>
        </div>
      ) : null}
    </div>
  )
}

export default ScoreSettingsTable
